import { useState, useEffect, useRef, useCallback } from 'react';
import useNotificationTimeout from './useNotificationTimeout';

const useNotifications = () => {
    const [notificationQueue, setNotificationQueue] = useState([]);
    const [currentNotification, setCurrentNotification] = useState(null);

    const currentNotificationRef = useRef(currentNotification);
    useEffect(() => {
        currentNotificationRef.current = currentNotification;
    }, [currentNotification]);

    const handleNotificationTimeout = useCallback(() => {
        if (currentNotificationRef.current?.onDismiss) {
            currentNotificationRef.current.onDismiss();
        }
        setCurrentNotification(null);
        setNotificationQueue(prev => {
            const newQueue = prev.slice(1);
            return newQueue;
        });
    }, []);

    useNotificationTimeout(
        currentNotification,
        handleNotificationTimeout,
        currentNotification?.persistent,
        2600
    );

    useEffect(() => {
        if (!currentNotification && notificationQueue.length > 0) {
            const nextNotification = notificationQueue[0];

            setTimeout(() => {
                setCurrentNotification({
                    ...nextNotification,
                    onClose: () => {
                        if (nextNotification.onDismiss) {
                            nextNotification.onDismiss();
                        }
                        setCurrentNotification(null);
                        setNotificationQueue(prev => prev.slice(1));
                    },
                });
            }, 0);
        }
    }, [notificationQueue, currentNotification]);

    const triggerNotification = useCallback(
        (message, type, persistent, onDismiss) => {
            const newNotification = {
                message,
                type,
                persistent,
                onDismiss,
                id: Date.now() + Math.random(),
            };

            if (!persistent) {
                setCurrentNotification({
                    ...newNotification,
                    onClose: () => {
                        if (newNotification.onDismiss) {
                            newNotification.onDismiss();
                        }
                        setCurrentNotification(null);
                    },
                });
                return;
            }

            setNotificationQueue(prev => {
                const queueHasDuplicate = prev.some(
                    notification => notification.message === message
                );
                const currentHasDuplicate =
                    currentNotification &&
                    currentNotification.message === message;
                const isDuplicate = queueHasDuplicate || currentHasDuplicate;

                if (isDuplicate) {
                    return prev;
                }

                return [...prev, newNotification];
            });
        },
        [currentNotification]
    );

    const clearNotification = useCallback(() => {
        setCurrentNotification(null);
        setNotificationQueue([]);
    }, []);

    return {
        currentNotification,
        notificationQueue,
        triggerNotification,
        clearNotification,
    };
};

export default useNotifications;
